import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import type { TooltipProps } from './Tooltip.types'
import Icon from '../icon/Icon'
import './tooltip.scss'

const Tooltip = ({
  children,
  position,
  align,
  active,
  text,
  image,
  cross,
  size,
  style,
}: TooltipProps) => {
  const hasCross = cross ? 'sb-tooltip--cross' : ''
  const [isOpen, setIsOpen] = useState(active)
  const isActive = isOpen ? 'sb-tooltip--open' : ''
  const [width, setwidth] = useState(0)

  const childrenRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsOpen(active)
  }, [active])

  useLayoutEffect(() => {
    if (childrenRef.current) {
      setwidth(childrenRef.current.getBoundingClientRect().width / 2)
    }
  }, [childrenRef, children])

  useEffect(() => {
    if (width && tooltipRef.current) {
      tooltipRef.current.style.setProperty('--width', `${width}px`)
    }
  }, [width, tooltipRef])

  return (
    <div className='sb-tooltip__container' data-testid='sb-tooltip__container'>
      <div
        className={[
          'sb-tooltip',
          `sb-tooltip--${position}`,
          `sb-tooltip--${align}`,
          `sb-tooltip--${size}`,
          isActive,
          hasCross,
        ].join(' ')}
        ref={tooltipRef}
        data-testid='sb-tooltip'
        style={{ ...style }}
      >
        {image && <img src={image} alt='' data-testid='sb-tooltip__img' />}
        {text}
        {cross && (
          <Icon
            className='sb-tooltip__close text-neutral-04 cursor-pointer'
            iconName='x-circle'
            size={13}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </div>
      <div
        className='sb-tooltip__children'
        data-testid='sb-tooltip__children'
        ref={childrenRef}
        onMouseOver={() => {
          setIsOpen(true)
        }}
        onMouseLeave={() => {
          !hasCross && (!active ? setIsOpen(false) : setIsOpen(true))
        }}
      >
        {children}
      </div>
    </div>
  )
}

Tooltip.propTypes = {
  /**
   * children
   */
  children: PropTypes.object,
  /**
   * position of tooltip
   */
  position: PropTypes.oneOf(['top', 'bottom', 'right', 'left']),
  /**
   * align of tooltip
   */
  align: PropTypes.oneOf(['init', 'middle', 'end']),
  /**
   * Size Tooltip
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * active
   */
  active: PropTypes.bool,
  /**
   * cross
   */
  cross: PropTypes.bool,
  /**
   * text
   */
  text: PropTypes.string,
  /**
   * image
   */
  image: PropTypes.string,
  /**
   * style
   */
  style: PropTypes.object,
}

Tooltip.defaultProps = {
  children: <div>Tooltip</div>,
  position: 'top',
  align: 'middle',
  active: false,
  text: '',
  image: '',
  cross: false,
  size: 'lg',
}

export default Tooltip
